'use client';

import clsx from 'clsx';
import { memo, useCallback, type MouseEvent } from 'react';
import { Check, Minus } from '@/components/ui/lucide-memo';

interface TableSelectionCheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: () => void;
  className?: string;
}

export const TableSelectionCheckbox = memo(function TableSelectionCheckbox({
  checked,
  indeterminate = false,
  disabled = false,
  onChange,
  className,
}: TableSelectionCheckboxProps) {
  const isMixed = indeterminate && !checked;

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!disabled) onChange();
    },
    [disabled, onChange],
  );

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={isMixed ? 'mixed' : checked}
      disabled={disabled}
      onClick={handleClick}
      className={clsx(
        // ChatGPT's checkbox: softer corners, the Launch bar's blue when on (owner, 2026-09-28).
        'inline-flex h-4 w-4 items-center justify-center rounded-[5px] border transition-colors',
        checked || isMixed
          ? 'border-[#3862C2] bg-[#3862C2] text-white'
          : 'border-white/20 bg-transparent text-transparent hover:border-white/40',
        disabled && 'cursor-not-allowed opacity-40',
        className,
      )}
    >
      {checked ? (
        <Check size={12} strokeWidth={3} />
      ) : isMixed ? (
        <Minus size={12} strokeWidth={3} />
      ) : null}
    </button>
  );
});
